// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { Dashboard as DashboardIcon } from '@mui/icons-material';
import PageSectionHeader from '../common/PageSectionHeader';

// Import dashboard components
import DashboardFilters from './DashboardFilters';
import SentimentAnalysisCard from './SentimentAnalysisCard';
import ScoreDistributionChart from './ScoreDistributionChart';
import QualificationTotalCard from './QualificationTotalCard';
import QualificationDistributionChart from './QualificationDistributionChart';
import RecordsTable from './RecordsTable';

// Import services
import { apiClient, API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const QADashboard = () => {
  // Auth context
  const { hasPermission, canFilterQADashboard, canExportQADashboard } = useAuth();
  
  // Helper function to format date for API (send as local date, not UTC)
  const formatDateForAPI = (date) => {
    if (!date) return undefined;
    
    // Create a date string in YYYY-MM-DD format for the start of the day in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    // Return as local date string (YYYY-MM-DD)
    return `${year}-${month}-${day}`;
  };
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [recordsData, setRecordsData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    teams: [],
    criteria: [],
    languages: []
  });

  // Filter state - criteria_type required; criteria_ids default to all of selected type
  const [filters, setFilters] = useState({
    criteria_type: '', // Required: 'matrix' | 'boolean'; set when filter options load
    team_ids: [],
    criteria_ids: [],
    language: 'all',
    date_from: new Date(),
    date_to: new Date()
  });

  // Pagination and sorting state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    sort_by: 'date',
    sort_order: 'desc'
  });

  // Load filter options on component mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Set default criteria_type and criteria_ids when filter options are loaded (do not overwrite user's type/criteria)
  // Note: teams may be empty (user not in UserTeam) but criteria can still exist; do not block on teams.
  useEffect(() => {
    if (filterOptions.criteria.length === 0) return;

    const allTeamIds = filterOptions.teams.map(t => t.id);
    const criteria = filterOptions.criteria;
    const hasMatrix = criteria.some(c => (c.criteria_type || 'matrix') === 'matrix');
    const hasBoolean = criteria.some(c => (c.criteria_type || 'matrix') === 'boolean');
    const defaultType = hasMatrix ? 'matrix' : hasBoolean ? 'boolean' : 'matrix';

    setFilters(prev => {
      const existingType = prev.criteria_type === 'matrix' || prev.criteria_type === 'boolean' ? prev.criteria_type : null;
      const type = existingType || defaultType;
      const criteriaOfType = criteria.filter(c => (c.criteria_type || 'matrix') === type);
      const idsOfType = criteriaOfType.map(c => c.id);
      const keepCriteriaIds = existingType && prev.criteria_ids && prev.criteria_ids.length > 0;

      return {
        ...prev,
        criteria_type: type,
        team_ids: prev.team_ids && prev.team_ids.length > 0 ? prev.team_ids : allTeamIds,
        criteria_ids: keepCriteriaIds ? prev.criteria_ids : idsOfType
      };
    });
  }, [filterOptions.teams, filterOptions.criteria]);

  // Load dashboard data when criteria_type is set and filter options have loaded
  useEffect(() => {
    if (filters.criteria_type && filterOptions.criteria.length > 0) {
      loadDashboardData();
    }
  }, [filterOptions.criteria, filters, pagination]);

  const loadFilterOptions = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.QA_DASHBOARD_FILTER_OPTIONS);
      if (response.data.success) {
        const data = response.data.data;
        
        // Deduplicate languages as safety net
        const uniqueLanguages = [...new Set(data.languages)].sort();
        
        setFilterOptions({
          ...data,
          languages: uniqueLanguages
        });
      }
    } catch (error) {
      console.error('Failed to load filter options:', error);
      setError('Failed to load filter options');
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load summary data
      const summaryResponse = await apiClient.get(API_ENDPOINTS.QA_DASHBOARD_SUMMARY, {
        params: {
          team_ids: filters.team_ids && filters.team_ids.length > 0 ? filters.team_ids.join(',') : 'all',
          criteria_ids: filters.criteria_ids && filters.criteria_ids.length > 0 ? filters.criteria_ids.join(',') : 'all',
          criteria_type: filters.criteria_type && filters.criteria_type !== 'all' ? filters.criteria_type : undefined,
          language: filters.language && filters.language !== 'all' ? filters.language : undefined,
          date_from: formatDateForAPI(filters.date_from),
          date_to: formatDateForAPI(filters.date_to)
        }
      });

      // Load records data
      const recordsResponse = await apiClient.get(API_ENDPOINTS.QA_DASHBOARD_RECORDS, {
        params: {
          team_ids: filters.team_ids && filters.team_ids.length > 0 ? filters.team_ids.join(',') : 'all',
          criteria_ids: filters.criteria_ids && filters.criteria_ids.length > 0 ? filters.criteria_ids.join(',') : 'all',
          criteria_type: filters.criteria_type && filters.criteria_type !== 'all' ? filters.criteria_type : undefined,
          language: filters.language && filters.language !== 'all' ? filters.language : undefined,
          date_from: formatDateForAPI(filters.date_from),
          date_to: formatDateForAPI(filters.date_to),
          page: pagination.page,
          limit: pagination.limit,
          sort_by: pagination.sort_by,
          sort_order: pagination.sort_order
        }
      });

      if (summaryResponse.data.success) {
        setSummaryData(summaryResponse.data.data);
      }

      if (recordsResponse.data.success) {
        setRecordsData(recordsResponse.data.data);
      }

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setPagination(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_order: sortOrder,
      page: 1 // Reset to first page when sorting
    }));
  };

  if (loading && !summaryData) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading QA Dashboard...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <PageSectionHeader
        icon={<DashboardIcon />}
        title="QA Dashboard"
        description="Monitor and analyze QA performance across your teams"
      />

      {/* Filters */}
      {canFilterQADashboard() && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <DashboardFilters
            filters={filters}
            filterOptions={filterOptions}
            onFilterChange={handleFilterChange}
          />
        </Paper>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards: Matrix QA (Sentiment + Score Distribution) */}
      {summaryData && hasPermission('QA_DASHBOARD_READ') && summaryData.matrix && summaryData.matrix.total_records > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
          <Box sx={{ flex: '0 0 300px', minWidth: '300px' }}>
            <SentimentAnalysisCard data={summaryData.matrix.sentiment_analysis} />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <ScoreDistributionChart
              data={summaryData.matrix.score_distribution}
              totalRecords={summaryData.matrix.total_records}
            />
          </Box>
        </Box>
      )}

      {/* Summary Cards: Boolean QA (Qualification Total + Distribution) */}
      {summaryData && hasPermission('QA_DASHBOARD_READ') && summaryData.boolean && summaryData.boolean.total_records > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
          <Box sx={{ flex: '0 0 300px', minWidth: '300px' }}>
            <QualificationTotalCard data={summaryData.boolean.qualification_total} />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <QualificationDistributionChart data={summaryData.boolean.qualification_total} />
          </Box>
        </Box>
      )}

      {/* Fallback: legacy top-level summary when no matrix/boolean split (backward compatibility) */}
      {summaryData && hasPermission('QA_DASHBOARD_READ') && (!summaryData.matrix || summaryData.matrix.total_records === 0) && (!summaryData.boolean || summaryData.boolean.total_records === 0) && (summaryData.sentiment_analysis || summaryData.score_distribution) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, width: '100%' }}>
          <Box sx={{ flex: '0 0 300px', minWidth: '300px' }}>
            <SentimentAnalysisCard data={summaryData.sentiment_analysis} />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <ScoreDistributionChart
              data={summaryData.score_distribution}
              totalRecords={summaryData.total_records}
            />
          </Box>
        </Box>
      )}

      {/* Records Table */}
      {recordsData && hasPermission('QA_DASHBOARD_READ') && (
        <Paper sx={{ p: 3 }}>
          <RecordsTable
            data={recordsData}
            loading={loading}
            onPaginationChange={handlePaginationChange}
            onSortChange={handleSortChange}
            canExport={canExportQADashboard()}
            onListRefreshSuggested={loadDashboardData}
          />
        </Paper>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={false}
        autoHideDuration={6000}
        message="Dashboard data loaded successfully"
      />
    </Container>
  );
};

export default QADashboard;
